import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetFootprintingPigletPrimaryDataById,
  useMutateFootprintingPigletPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_PIGLET_FOOTPRINTING_MOORLAND_ACTIVE, FARM_FOOTPRINTING_PIGLET_MOORLAND } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin, Typography } from "components";

interface MoorlandProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const PigletMoorlandForm = ({ primaryDataId, readOnly, farmId, year }: MoorlandProps) => {
  const { t } = useTranslation("footprintingPiglet");
  const formRef = useRef<FormInstance>(null);

  const [moorlandActive, setMoorlandActive] = useState<ENUM_PIGLET_FOOTPRINTING_MOORLAND_ACTIVE | null>(null);
  const [moorlandAreaForFodderProduction, setFodderProductionAreaHa] = useState<number | null>(null);
  const [moorlandAnnualWaterLevel, setannualWaterLevelCm] = useState<number | null>(null);
  const pendingValidateRef = useRef(false);

  const { data: pigletPrimaryData, isLoading, isSuccess } = useGetFootprintingPigletPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutatePigletPrimaryData } = useMutateFootprintingPigletPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      moorlandActive,
      moorlandAreaForFodderProduction,
      moorlandAnnualWaterLevel,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePigletPrimaryData({});
      }
    },
    [isLoading, mutatePigletPrimaryData, readOnly]
  );

  useEffect(() => {
    if (pigletPrimaryData && isSuccess) {
      setMoorlandActive(pigletPrimaryData.moorlandActive ?? null);
      setFodderProductionAreaHa(pigletPrimaryData.moorlandAreaForFodderProduction ?? null);
      setannualWaterLevelCm(pigletPrimaryData.moorlandAnnualWaterLevel ?? null);

      formRef.current?.setFieldsValue({
        fodderProductionAreaHa: pigletPrimaryData.moorlandAreaForFodderProduction ?? null,
        annualWaterLevelCm: pigletPrimaryData.moorlandAnnualWaterLevel ?? null,
      });

      if (pigletPrimaryData.moorlandActive === FARM_FOOTPRINTING_PIGLET_MOORLAND.YES) {
        pendingValidateRef.current = true;
      }
    }
  }, [pigletPrimaryData, isSuccess]);

  // Fires on the render where moorlandActive actually becomes "yes" (i.e. after the state update
  // from the load effect above has committed and the gated fields have mounted) — not in the same
  // effect-flush pass as that update, which is too early for the fields to be registered yet.
  useEffect(() => {
    if (!pendingValidateRef.current) return;
    if (moorlandActive !== FARM_FOOTPRINTING_PIGLET_MOORLAND.YES) return;
    pendingValidateRef.current = false;
    formRef.current?.validateFields();
  }, [moorlandActive]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-piglet-moorland-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={24}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("pigletMoorlandForm.question.moorlandRadioQuestion")}
              {true && <span className="required-asterisk">*</span>}
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-piglet-moorland-radio"
              onChange={({ target }) => {
                setMoorlandActive(target.value);
              }}
              value={moorlandActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_PIGLET_MOORLAND.YES, FARM_FOOTPRINTING_PIGLET_MOORLAND.NO].map((moorland) => ({
                label: t(`pigletMoorlandForm.moorlandOptions.${moorland}`),
                value: moorland,
              }))}
            />
          </Flex>

          {moorlandActive === FARM_FOOTPRINTING_PIGLET_MOORLAND.YES && (
            <Flex vertical gap={16}>
              <InputNumber
                disabled={readOnly}
                name="fodderProductionAreaHa"
                dataTestId="footprinting-piglet-fodder-production-area-input"
                label={t("pigletMoorlandForm.question.fodderProductionAreaLabel")}
                suffix={t("pigletMoorlandForm.question.fodderProductionAreaSuffix")}
                value={moorlandAreaForFodderProduction}
                initialValue={moorlandAreaForFodderProduction}
                onChange={(v) => setFodderProductionAreaHa(v)}
                validateFirst
                guidanceText={t("pigletMoorlandForm.notSure")}
                rules={[
                  { required: true, type: "number", message: t("pigletMoorlandForm.error.validNumber") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("pigletMoorlandForm.error.validNumber")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />

              <InputNumber
                disabled={readOnly}
                name="annualWaterLevelCm"
                dataTestId="footprinting-piglet-annual-water-lever-input"
                label={t("pigletMoorlandForm.question.annualWaterLevelLabel")}
                suffix={t("pigletMoorlandForm.question.annualWaterLevelSuffix")}
                value={moorlandAnnualWaterLevel}
                initialValue={moorlandAnnualWaterLevel}
                onChange={(v) => setannualWaterLevelCm(v)}
                validateFirst
                guidanceText={t("pigletMoorlandForm.notSure")}
                rules={[
                  { required: true, type: "number", message: t("pigletMoorlandForm.error.validNumber") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("pigletMoorlandForm.error.validNumber")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

PigletMoorlandForm.Key = "piglet.moorland";
export { PigletMoorlandForm };
